/**
 * Calculates the bottom position value based on the safe area bottom.
 *
 * Logic:
 * - If safe area bottom is 0 or less than 0.5rem → 0.5rem (minimum spacing)
 * - If safe area bottom is between 0.5rem and 1rem → safe area bottom (use as-is)
 * - If safe area bottom is more than 1rem → safe area bottom - 0.5rem (reduce by 0.5rem)
 *
 * This ensures:
 * - Minimum 0.5rem spacing from the bottom edge
 * - When safe area is large (>1rem), component sits 0.5rem above the safe area
 *
 * @returns CSS expression for the bottom position
 */
/**
 * Read `--safe-area-inset-bottom` before Ionic's `--ion-safe-area-bottom`.
 *
 * Ionic derives its variable from `env(safe-area-inset-bottom)`, which Capacitor's
 * SystemBars plugin zeroes out inside the WebView on Android; it publishes the real
 * inset as `--safe-area-inset-bottom` instead. See `src/utils/safe-area-classes.ts`
 * for the full explanation. `env()` stays last so web/PWA still works.
 */
const SAFE_AREA_BOTTOM =
  "var(--safe-area-inset-bottom, var(--ion-safe-area-bottom, env(safe-area-inset-bottom, 0px)))";

export const getSafeAreaBottom = (): string => {
  // Use max() with three values to implement the piecewise function:
  // - 0.5rem: minimum value
  // - min(safeArea, 1rem): caps at 1rem for middle range
  // - safeArea - 0.5rem: used when safeArea > 1.5rem
  return `max(0.5rem, min(${SAFE_AREA_BOTTOM}, 1rem), calc(${SAFE_AREA_BOTTOM} - 0.5rem))`;
};
